import type { TFunction } from 'i18next';
import { AutomationStatusEnum } from '@electricjs/core_entity-client';
import { Request } from '@/types/requests';

// Calendar-day difference in the browser's local timezone, matching how
// formatDate parses and displays date-only strings (local midnight).
const differenceInLocalCalendarDays = (later: Date, earlier: Date): number => {
  const localMidnight = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  return Math.round(
    (localMidnight(later) - localMidnight(earlier)) / (1000 * 60 * 60 * 24)
  );
};

// Parses a date-only string (e.g. "2026-07-28") as local midnight, same
// convention as formatDate, so day comparisons stay in sync with the date
// as displayed to the user.
const parseLocalDate = (dateOnly: string): Date =>
  new Date(`${dateOnly}T00:00:00`);

type OnboardingTooltipParams = {
  canOnboard: boolean;
  hasEmail: boolean;
  hasHrisIntegration: boolean;
  hasScheduledOnboardingInFlight: boolean;
  t: TFunction<'requests', undefined>;
};

/**
 * Generates tooltip content for onboarding actions based on user permissions and employee data.
 *
 * @param canOnboard - Whether the user has permission to onboard employees
 * @param hasEmail - Whether the employee has a work email
 * @param hasHrisIntegration - Whether the organization has HRIS integration
 * @param hasScheduledOnboardingInFlight - Whether the employee has a scheduled onboarding request in flight
 * @param t - Translation function for the 'requests' namespace
 * @returns The tooltip content string, or empty string if no tooltip should be shown
 */
export const getOnboardingTooltipContent = ({
  canOnboard,
  hasEmail,
  hasHrisIntegration,
  hasScheduledOnboardingInFlight,
  t,
}: OnboardingTooltipParams): string => {
  if (!canOnboard) {
    return t('requests:taskActions.hrisOnboarding.noPermissionTooltip');
  }

  if (!hasEmail) {
    const noEmailMessage = t(
      'requests:taskActions.hrisOnboarding.noEmailTooltip'
    );
    const hrisMessage = hasHrisIntegration
      ? ` ${t('requests:taskActions.hrisOnboarding.hrisIntegrationTooltip')}`
      : '';
    return `${noEmailMessage}${hrisMessage}`;
  }

  if (hasScheduledOnboardingInFlight) {
    return t('requests:taskActions.hrisOnboarding.onboardingInFlightTooltip');
  }

  return '';
};

/**
 * Derives whether an onboarding has a scheduled (delayed automation) request
 * in flight, and the earliest date at which one will fire.
 *
 * @param requests - The requests belonging to an onboarding
 */
export const getScheduledOnboardingInfo = (requests?: Request[]) => {
  const scheduledRequests =
    requests?.filter(
      request => request.automationStatus === AutomationStatusEnum.Scheduled
    ) ?? [];

  const nextScheduledDate = scheduledRequests.reduce<string | undefined>(
    (earliest, request) =>
      request.notBefore &&
      (!earliest ||
        new Date(request.notBefore).getTime() < new Date(earliest).getTime())
        ? request.notBefore
        : earliest,
    undefined
  );

  return {
    hasScheduledOnboardingInFlight: scheduledRequests.length > 0,
    nextScheduledDate,
  };
};

/**
 * Whether an employee's start date has already passed (local calendar day),
 * so copy referring to it can be phrased in the past tense. Matches the
 * local-midnight parsing formatDate uses to display the same date.
 *
 * @param startDate - The employee's start date
 */
export const hasStartDatePassed = (startDate?: string): boolean => {
  if (!startDate) {
    return false;
  }

  return (
    differenceInLocalCalendarDays(new Date(), parseLocalDate(startDate)) > 0
  );
};

/**
 * Whether a scheduled onboarding request's run time has already passed.
 *
 * @param nextScheduledDate - The earliest date at which a scheduled request will fire
 */
export const hasScheduledRunPassed = (nextScheduledDate?: string): boolean => {
  if (!nextScheduledDate) {
    return false;
  }

  return new Date(nextScheduledDate).getTime() <= Date.now();
};

/**
 * Derives the countdown copy for a scheduled onboarding: how many days until
 * the scheduled request runs, and how many calendar days before the
 * employee's start date that is.
 *
 * @param nextScheduledDate - The earliest date at which a scheduled request will fire
 * @param startDate - The employee's start date
 */
export const getOnboardingScheduleCountdown = (
  nextScheduledDate?: string,
  startDate?: string
): { daysUntilRun?: number; daysBeforeStart?: number } => {
  if (!nextScheduledDate) {
    return {};
  }

  const scheduledDate = new Date(nextScheduledDate);

  return {
    daysUntilRun: differenceInLocalCalendarDays(scheduledDate, new Date()),
    daysBeforeStart: startDate
      ? differenceInLocalCalendarDays(parseLocalDate(startDate), scheduledDate)
      : undefined,
  };
};
